import { lazy, Suspense, type ComponentType } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';
import { AppShell } from '../layout/AppShell';
import { RouteErrorElement } from '../layout/RouteErrorElement';
import { AuthGuard } from '../components/auth/AuthGuard';
import { AdminGuard } from '../components/auth/AdminGuard';
import { ResearchGuard } from '../components/auth/ResearchGuard';
import { FeatureGuard } from '../components/auth/FeatureGuard';
import { SetupGuard } from '../components/auth/SetupGuard';
import { MaintenanceGuard } from '../components/auth/MaintenanceGuard';
import { PageLoader } from '../components/common/PageLoader';

// Lazy-loaded pages — each gets its own chunk for code splitting.
const DashboardPage = lazy(() => import('../pages/DashboardPage'));
const ChartPage = lazy(() => import('../pages/ChartPage'));
const ChartViewport = lazy(() => import('../pages/chart/modular/ChartViewport'));
const SystemPage = lazy(() => import('../pages/SystemPage'));
const StoragePage = lazy(() => import('../pages/StoragePage'));
const SettingsPage = lazy(() => import('../pages/SettingsPage'));
const NotificationsSettingsPage = lazy(() => import('../pages/NotificationsSettingsPage'));
const SentimentPage = lazy(() => import('../pages/SentimentPage'));
const ResearchPage = lazy(() => import('../pages/research/ResearchPage'));
const GatePage = lazy(() => import('../pages/gate/GatePage'));
const StrategyBuilderPage = lazy(() => import('../pages/StrategyBuilderPage'));
const WalletPage = lazy(() => import('../pages/WalletPage'));
const BacktestPage = lazy(() => import('../pages/BacktestPage'));
const ApiKeysPage = lazy(() => import('../pages/ApiKeysPage'));
const AdminPage = lazy(() => import('../pages/AdminPage'));
const TradingDashboardPage = lazy(() => import('../pages/TradingDashboardPage'));
const TradingHubPage = lazy(() => import('../pages/TradingHubPage'));
const StrategiesHubPage = lazy(() => import('../pages/StrategiesHubPage'));
const AutomatedTradingPage = lazy(() => import('../pages/AutomatedTradingPage'));
const TradeJournalPage = lazy(() => import('../pages/TradeJournalPage'));
const WalletComparePage = lazy(() => import('../pages/WalletComparePage'));
const LoginPage = lazy(() => import('../pages/LoginPage'));
const RegisterPage = lazy(() => import('../pages/RegisterPage'));
const SetupPage = lazy(() => import('../pages/SetupPage'));
const TermsPage = lazy(() => import('../pages/TermsPage'));
const PrivacyPage = lazy(() => import('../pages/PrivacyPage'));


/** Wrap a lazy component in Suspense with the page skeleton loader. */
function L({ C }: { C: ComponentType }) {
  return (
    <Suspense fallback={<PageLoader />}>
      <C />
    </Suspense>
  );
}

export const router = createBrowserRouter([
  // Auth pages render without the app shell (no sidebar/footer)
  { path: '/login', element: <L C={LoginPage} />, errorElement: <RouteErrorElement /> },
  { path: '/register', element: <L C={RegisterPage} />, errorElement: <RouteErrorElement /> },
  { path: '/setup', element: <L C={SetupPage} />, errorElement: <RouteErrorElement /> },
  { path: '/terms', element: <L C={TermsPage} />, errorElement: <RouteErrorElement /> },
  { path: '/privacy', element: <L C={PrivacyPage} />, errorElement: <RouteErrorElement /> },


  // All app pages — blocked by SetupGuard until first admin exists
  {
    path: '/',
    element: (
      <SetupGuard>
        <MaintenanceGuard>
          <AppShell />
        </MaintenanceGuard>
      </SetupGuard>
    ),
    errorElement: <RouteErrorElement />,
    children: [
      // Public — no auth required. Each feature route is wrapped in FeatureGuard
      // so an admin can disable it or scope it to a tier; the underlying access
      // guard (Auth/Research) still applies underneath.
      { index: true, element: <FeatureGuard feature="dashboard"><L C={DashboardPage} /></FeatureGuard> },
      // /chart is the modular dashboard (Phase 3b cutover). ChartPage owns symbol
      // ↔ route sync and renders ChartViewport; :symbol resolves the active symbol.
      { path: 'chart', element: <FeatureGuard feature="chart"><L C={ChartPage} /></FeatureGuard> },
      { path: 'chart/:symbol', element: <FeatureGuard feature="chart"><L C={ChartPage} /></FeatureGuard> },
      // Alias to the bare modular viewport (no symbol-route sync wrapper). Kept for
      // existing deep links; /chart is the canonical entry.
      { path: 'chart-next', element: <FeatureGuard feature="chart"><L C={ChartViewport} /></FeatureGuard> },
      { path: 'sentiment', element: <FeatureGuard feature="sentiment"><L C={SentimentPage} /></FeatureGuard> },
      // Research terminal — gated by an explicit researchTier (admin role does
      // NOT imply access). No-tier users see an in-place "access required" panel
      // (ResearchGuard) rather than a redirect. FeatureGuard ('hunt') layers the
      // admin on/off + tier override ON TOP, so disabling Hunt blocks it for
      // everyone — matching the server-side launch enforcement.
      {
        path: 'research',
        element: (
          <FeatureGuard feature="hunt">
            <ResearchGuard><L C={ResearchPage} /></ResearchGuard>
          </FeatureGuard>
        ),
      },
      {
        path: 'research/:iterationId',
        element: (
          <FeatureGuard feature="hunt">
            <ResearchGuard><L C={ResearchPage} /></ResearchGuard>
          </FeatureGuard>
        ),
      },

      // Section landing hubs — the mobile footer's Trading/Strategies tabs land
      // here. Public surfaces: the cards self-gate (auth-lock / feature-flag /
      // market) via resolveSectionCards, so the route needs no guard. On desktop
      // the sidebar links straight to the underlying pages; the hub still works
      // as a deep link and simply widens to a multi-column grid.
      { path: 'trading-hub', element: <L C={TradingHubPage} /> },
      { path: 'strategies-hub', element: <L C={StrategiesHubPage} /> },

      // Auth required — logged-in users
      {
        path: 'trading',
        element: <FeatureGuard feature="trading"><AuthGuard><L C={TradingDashboardPage} /></AuthGuard></FeatureGuard>,
      },
      {
        path: 'automated',
        element: <FeatureGuard feature="automatedTrading"><AuthGuard><L C={AutomatedTradingPage} /></AuthGuard></FeatureGuard>,
      },
      {
        path: 'journal',
        element: <FeatureGuard feature="journal"><AuthGuard><L C={TradeJournalPage} /></AuthGuard></FeatureGuard>,
      },
      {
        path: 'wallet-compare',
        element: <FeatureGuard feature="walletCompare"><AuthGuard><L C={WalletComparePage} /></AuthGuard></FeatureGuard>,
      },
      {
        path: 'strategy',
        element: <FeatureGuard feature="strategyBuilder"><AuthGuard><L C={StrategyBuilderPage} /></AuthGuard></FeatureGuard>,
      },
      {
        path: 'backtest',
        element: <FeatureGuard feature="backtest"><AuthGuard><L C={BacktestPage} /></AuthGuard></FeatureGuard>,
      },
      {
        path: 'wallet',
        element: <FeatureGuard feature="wallet"><AuthGuard><L C={WalletPage} /></AuthGuard></FeatureGuard>,
      },
      {
        path: 'api-keys',
        element: <FeatureGuard feature="apiKeys"><AuthGuard><L C={ApiKeysPage} /></AuthGuard></FeatureGuard>,
      },
      {
        path: 'settings',
        element: <AuthGuard><L C={SettingsPage} /></AuthGuard>,
      },
      {
        path: 'settings/notifications',
        element: <AuthGuard><L C={NotificationsSettingsPage} /></AuthGuard>,
      },

      // Admin required
      {
        path: 'system',
        element: <AdminGuard><L C={SystemPage} /></AdminGuard>,
      },
      {
        path: 'storage',
        element: <AdminGuard><L C={StoragePage} /></AdminGuard>,
      },
      {
        path: 'gate',
        element: <AdminGuard><L C={GatePage} /></AdminGuard>,
      },
      {
        path: 'admin',
        element: <AdminGuard><L C={AdminPage} /></AdminGuard>,
      },

      // Redirects for removed routes
      { path: 'signals', element: <Navigate to="/strategy" replace /> },
      { path: 'signals/:symbol', element: <Navigate to="/strategy" replace /> },
      // Discovery (indicator scanner) and Screener were retired in the
      // nav restructure. Send any lingering deep links somewhere useful.
      { path: 'discovery', element: <Navigate to="/research" replace /> },
      { path: 'screener', element: <Navigate to="/strategy" replace /> },
    ],
  },
]);
